import { useCallback, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { map, union, without } from "lodash";
import { AiccManagedLanguage, AiccManagedLanguageList } from "types/model/aiccLanguage";
import * as aiccLanguageAPI from "services/aiccLanguage";
import { aiccLanguageContentVersionsKey } from "./useAiccLanguageContentVersions";
import { AICC_SWR_CONFIG } from "./swrConfig";

/**
 * Languages under AICC management, each with its releases — but not the fourteen asset breakdowns of
 * its readable copies, which the list is explicitly requested without. Those cost the backend ~1.4s
 * per language and every card starts collapsed, so an expanded card reads its own
 * (`useAiccLanguageContentVersions`).
 *
 * Writes are not SWR-managed: the service mutations run and then the caller refreshes. Which
 * refresh it wants depends on what changed —
 *
 * - `refreshLanguage` for anything that changes one language, which is every mutation except add
 *   and delete. It re-reads that language alone rather than all fifty, and re-reads rather than
 *   trusting the mutation's own body, since the row has to match what the next reader of the list
 *   would see.
 * - `mutate` for add and delete, where a row appears or disappears and no single-language response
 *   can say what the rest of the list now looks like.
 */
export function useAiccManagedLanguages() {
  const { mutate: mutateKey } = useSWRConfig();
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    ["aicc/managed-languages"],
    () => aiccLanguageAPI.fetchAiccManagedLanguages(),
    AICC_SWR_CONFIG
  );

  const replaceLanguage = useCallback(
    (updated: AiccManagedLanguage) =>
      mutate(
        // Annotated because swr's own types cannot be imported here: TS 4.1 fails to parse
        // them, so nothing would infer this parameter and it would silently become `any`.
        (current?: AiccManagedLanguageList) => ({
          items: map(current?.items, (language) => (language.id === updated.id ? updated : language)),
          total: current?.total ?? 0,
        }),
        // The response is the new truth, so asking the server for it again would only
        // reintroduce the request this exists to avoid.
        { revalidate: false }
      ),
    [mutate]
  );

  /**
   * Which rows are being re-read. A list of ids rather than a boolean: two cards can be acted on
   * without waiting for each other, and a shared flag would put the indicator on both.
   */
  const [refreshingLanguageIds, setRefreshingLanguageIds] = useState<number[]>([]);

  const refreshLanguage = useCallback(
    (languageId: number) => {
      setRefreshingLanguageIds((ids) => union(ids, [languageId]));

      return aiccLanguageAPI
        .fetchAiccManagedLanguage(languageId)
        .then((updated) => {
          // `/managed` is read in full, so its copies are newer than whatever the expanded card
          // fetched. Writing them into that cache is what makes an open row show the result of an
          // action — without it the row would keep serving the copies from before the action, and
          // asking the server for them again would be a second 2.6s request for data already in hand.
          if (updated.contentVersions) {
            mutateKey(aiccLanguageContentVersionsKey(updated.id), updated.contentVersions, { revalidate: false });
          }

          return replaceLanguage(updated);
        })
        .finally(() => setRefreshingLanguageIds((ids) => without(ids, languageId)));
    },
    [mutateKey, replaceLanguage]
  );

  return {
    languages: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading,
    /**
     * Re-reading a list that is already on screen. Separate from `isLoading`, which SWR only reports
     * while there is nothing to show — so after adding or deleting a language the page had data, no
     * loading flag, and nothing at all to say a request was in flight.
     *
     * Only a full `mutate` sets this: `refreshLanguage` writes the cache with `revalidate: false`, so
     * a single row updating in place does not put the whole page in a loading state.
     */
    isRefreshing: isValidating && !isLoading,
    /** Ids of the rows a `refreshLanguage` is in flight for, so each card can say so on its own. */
    refreshingLanguageIds,
    isError: !!error,
    mutate,
    refreshLanguage,
  };
}
